/**
 * Focus Tasks Generator
 * Generates dynamic focus tasks based on real data
 */

import type { FocusTask, SubmissionCollege, SupplementProgress } from "./types";

/**
 * Generate dynamic focus tasks based on real data
 */
export function generateFocusTasks(
  roadToSubmissionColleges: SubmissionCollege[],
  detailedSupplements: SupplementProgress[],
  activityComposition: Record<string, number>,
): FocusTask[] {
  const tasks: FocusTask[] = [];
  const today = new Date();

  // 1. RED TASK: Most urgent deadline(s)
  if (roadToSubmissionColleges.length > 0) {
    // Filter to only future deadlines and sort by deadline
    const upcomingColleges = roadToSubmissionColleges
      .filter((c) => new Date(c.deadline) >= today)
      .sort(
        (a, b) =>
          new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
      );

    if (upcomingColleges.length > 0) {
      const earliestDeadline = upcomingColleges[0].deadline;
      // Find all colleges with the same earliest deadline
      const collegesWithSameDeadline = upcomingColleges.filter(
        (c) => c.deadline === earliestDeadline,
      );

      // Calculate days until deadline
      const deadlineDate = new Date(earliestDeadline);
      const daysUntil = Math.ceil(
        (deadlineDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );

      // Build title with college names and decision types
      let title: string;
      if (collegesWithSameDeadline.length === 1) {
        const college = collegesWithSameDeadline[0];
        const shortName = college.name.split(" ").slice(0, 2).join(" ");
        title = `Submit ${shortName} ${college.decisionType} application`;
      } else if (collegesWithSameDeadline.length === 2) {
        const name1 = collegesWithSameDeadline[0].name.split(" ")[0];
        const name2 = collegesWithSameDeadline[1].name.split(" ")[0];
        const type = collegesWithSameDeadline[0].decisionType;
        title = `Submit ${name1} and ${name2} ${type} applications`;
      } else {
        const type = collegesWithSameDeadline[0].decisionType;
        title = `Submit ${collegesWithSameDeadline.length} ${type} applications`;
      }

      // Build subtitle
      let subtitle: string;
      if (daysUntil === 0) {
        subtitle = "Due today!";
      } else if (daysUntil === 1) {
        subtitle = "Due tomorrow";
      } else {
        subtitle = `Due in ${daysUntil} days`;
      }

      tasks.push({
        id: "focus-red",
        title,
        subtitle,
        dueDate: earliestDeadline,
        status: "overdue", // Red task is always the most urgent - show as overdue/red
        module: "calendar",
        type: "deadline",
      });
    }
  }

  // 2. YELLOW TASK: Incomplete supplements for college closest to deadline
  if (detailedSupplements.length > 0 && roadToSubmissionColleges.length > 0) {
    // Find supplements that are not 100% complete
    const incompleteSupplements = detailedSupplements.filter(
      (s) => s.total > 0 && s.completed < s.total,
    );

    if (incompleteSupplements.length > 0) {
      // Match with colleges to find deadlines and sort by deadline
      const supplementsWithDeadlines = incompleteSupplements
        .map((supp) => {
          // Find matching college in roadToSubmission
          const matchingCollege = roadToSubmissionColleges.find((c) => {
            const collegeId = c.id.split("-")[0]; // Extract base college ID
            return String(collegeId) === String(supp.collegeId);
          });
          return {
            ...supp,
            deadline: matchingCollege?.deadline || "2099-12-31",
            collegeName: supp.collegeName || matchingCollege?.name || "Unknown",
          };
        })
        .sort(
          (a, b) =>
            new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
        );

      // Pick the one closest to deadline
      const urgentSupplement = supplementsWithDeadlines[0];
      const remaining = urgentSupplement.total - urgentSupplement.completed;
      const shortName = urgentSupplement.collegeName
        .split(" ")
        .slice(0, 2)
        .join(" ");

      tasks.push({
        id: "focus-yellow",
        title: `Complete ${shortName} Supplemental Essays`,
        subtitle: `${remaining} essay${remaining > 1 ? "s" : ""} remaining`,
        status: "upcoming",
        module: "supplements",
        type: "supplement",
      });
    }
  }

  // 3. BLUE TASK: Activity improvement suggestion
  // Use the same categories as shown in the Activities composition breakdown
  const activityCategories = [
    "Academic & Intellectual",
    "Arts & Culture",
    "Leadership & Community",
    "Work & Family",
    "Athletics",
    "Other",
  ];

  // Check if we have any composition data
  const hasCompositionData = Object.keys(activityComposition || {}).length > 0;

  if (hasCompositionData) {
    // Find all categories with the lowest percentage (likely 0%)
    let lowestPercent = 100;

    // First pass: find the lowest percentage
    for (const category of activityCategories) {
      const percent = activityComposition[category] || 0;
      if (percent < lowestPercent) {
        lowestPercent = percent;
      }
    }

    // Second pass: collect all categories with that lowest percentage
    const lowestCategories = activityCategories.filter(
      (cat) => (activityComposition[cat] || 0) === lowestPercent,
    );

    // Build a nice title with the lacking categories (use short names)
    const shortName = (cat: string) => {
      // Convert to shorter display names
      if (cat === "Academic & Intellectual") return "Academic";
      if (cat === "Arts & Culture") return "Arts";
      if (cat === "Leadership & Community") return "Leadership";
      if (cat === "Work & Family") return "Work";
      return cat;
    };

    // Only show suggestion if there are categories with low representation
    if (lowestCategories.length > 0 && lowestPercent < 20) {
      let title: string;
      if (lowestCategories.length === 1) {
        title = `Add More ${shortName(lowestCategories[0])} Activities`;
      } else if (lowestCategories.length === 2) {
        title = `Add ${shortName(lowestCategories[0])} or ${shortName(lowestCategories[1])} Activities`;
      } else {
        // 3+ categories - list first two
        title = `Add ${shortName(lowestCategories[0])} or ${shortName(lowestCategories[1])} Activities`;
      }

      tasks.push({
        id: "focus-blue",
        title,
        subtitle: "Low urgency • strengthens your profile",
        status: "low",
        module: "activities",
        type: "activity",
      });
    } else {
      // All categories have decent representation
      tasks.push({
        id: "focus-blue",
        title: "Review Your Activities Section",
        subtitle: "Low urgency • nice to have",
        status: "low",
        module: "activities",
        type: "activity",
      });
    }
  } else {
    // No activities yet
    tasks.push({
      id: "focus-blue",
      title: "Add Your First Activity",
      subtitle: "Get started on your activities list",
      status: "low",
      module: "activities",
      type: "activity",
    });
  }

  return tasks;
}
